import React, { useEffect, useMemo, useState } from 'react';
// Adjust this import to your actual hook path/name:
import { useTenantUserInfo } from '../../context/userInfoProvider';
// Adjust this import/path to match your S3 url helper:
import { S3url } from '../../components/TenantApp/s3url';

type InventoryItem = {
  id: string;
  description: string;
  photo: string;
  // add other fields if you need them
};

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
  marginBottom: 16,
  flexWrap: 'wrap',
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const sortLabel: React.CSSProperties = {
  fontSize: 13,
  marginRight: 8,
  color: '#E2E4FF',
};

const sortSelect: React.CSSProperties = {
  backgroundColor: '#1F0F30',
  borderRadius: 6,
  border: '1px solid #61518A',
  padding: '6px 8px',
  color: '#F5F5FA',
  fontSize: 13,
};

const refreshButton: React.CSSProperties = {
  marginLeft: 8,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #61518A',
  backgroundColor: '#2A1641',
  color: '#F5F5FA',
  fontSize: 12,
  cursor: 'pointer',
};

const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: 16,
  marginTop: 8,
};

const itemCard: React.CSSProperties = {
  backgroundColor: '#FFFFFF',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  color: '#111827',
};

const imageWrapper: React.CSSProperties = {
  width: '100%',
  paddingTop: '100%', // square
  position: 'relative',
  overflow: 'hidden',
};

const imageStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const itemBody: React.CSSProperties = {
  padding: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const itemTitle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
};

const itemMeta: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.8,
};

const emptyState: React.CSSProperties = {
  marginTop: 32,
  textAlign: 'center',
  fontSize: 14,
  opacity: 0.85,
};

type SortOption = '' | 'Newest' | 'Alphabetical';

const StorageInventoryPage: React.FC = () => {
  const { dataSourceStorage = [], fetchData } = useTenantUserInfo() as {
    dataSourceStorage?: InventoryItem[];
    fetchData: () => Promise<void>;
  };

  const [sortBy, setSortBy] = useState<SortOption>(''); // default = backend order (likely newest)
  const [isRefreshing, setIsRefreshing] = useState(false);

  // initial load
  useEffect(() => {
    fetchData().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(() => {
    const copy = [...dataSourceStorage];
    if (sortBy === 'Alphabetical') {
      copy.sort((a, b) => a.description.localeCompare(b.description));
    } else if (sortBy === 'Newest') {
      // assume backend default is newest first;
      // if not, flip based on createdAt or id here
      // copy.sort((a, b) => Number(b.id) - Number(a.id));
    }
    return copy;
  }, [dataSourceStorage, sortBy]);

  const handleSortChange = (value: SortOption) => {
    setSortBy(value);
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await fetchData();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerRow}>
          <h1 style={titleStyle}>Storage Inventory</h1>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={sortLabel} htmlFor="storage-inventory-sort">
              Sort by
            </label>
            <select
              id="storage-inventory-sort"
              style={sortSelect}
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value as SortOption)}
            >
              <option value="">Default</option>
              <option value="Newest">Date Added (Newest)</option>
              <option value="Alphabetical">A → Z</option>
            </select>
            <button
              type="button"
              style={refreshButton}
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div style={emptyState}>
            You don&apos;t have any items currently stored with Bins.
            <br />
            Once your items are picked up and checked in, they&apos;ll show up here.
          </div>
        ) : (
          <div style={grid}>
            {items.map((item) => (
              <div key={item.id} style={itemCard}>
                <div style={imageWrapper}>
                  <img
                    style={imageStyle}
                    src={`${S3url}${item.photo}`}
                    alt={item.description}
                  />
                </div>
                <div style={itemBody}>
                  <div style={itemTitle}>
                    {item.description?.slice(0, 12) || 'Untitled item'}
                  </div>
                  <div style={itemMeta}>
                    ID #{item.id?.slice(0, 12) || 'N/A'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StorageInventoryPage;
